<template>
  <div
    class="MyMapLayerPicker"
    :class="{ 'MyMapLayerPicker--indicatorStyle': isIndicatorStyle }"
    v-if="mainLayer"
  >
    <div
      class="MyMapLayerPicker__parentBlock"
      :class="{
        MyMapIndicateur: isIndicatorStyle,
        'MyMapIndicateur--active': mainLayer.isShown && isIndicatorStyle,
        'MyMapLayerPicker__parentBlock--platformInnerRow': primaryParentBorder,
      }"
    >
      <template v-if="isIndicatorStyle">
        <div class="MyMapIndicateur__content" @click="toggleOsmLayer">
          <div class="MyMapIndicateur__icon">
            <!-- Styled OSM layers: swatch renders sprite icon, polygon rect, or line.
                 Also mounted when a non-empty marker_config exists (new-format type d'ouvrage rows
                 have no geometryType), so the swatch can render the configured marker instead. -->
            <LayerStyleSwatch
              v-if="mainLayer.style?.geometryType || hasMarkerConfig"
              :layer-style="mainLayer.style"
            />
            <!-- Fond layers and layers without style info: generic icon -->
            <Icon
              v-else
              :icon="mainLayer.icon ?? '$mapMarkerOutline'"
              :color="categoryIconColor"
              :size="18"
            />
          </div>
          <div class="MyMapLayerPicker__checkbox MyMapIndicateur__text">
            <span class="MyMapIndicateur__titre">
              <span class="MyMapIndicateur__titreText">{{ mainLayer.name }}</span>
              <span v-if="countLabel !== undefined" class="MyMapLayerPicker__countBadge">{{
                countLabel
              }}</span>
              <span v-else-if="dataSourceLabel" class="MyMapLayerPicker__dataSource">{{
                dataSourceLabel
              }}</span>
            </span>
          </div>
          <div class="MyMapIndicateur__actionsColumn" @click.stop>
            <slot name="extra-action">
              <button
                type="button"
                class="MyMapIndicateur__toggleButton"
                :aria-label="
                  mainLayer.isShown
                    ? $t('myMap.rightSidebar.hideLayer')
                    : $t('myMap.rightSidebar.showLayer')
                "
                @click.stop="toggleOsmLayer"
              >
                <Icon
                  :icon="mainLayer.isShown ? 'mdi:eye' : 'mdi:eye-off'"
                  :color="mainLayer.isShown ? categoryIconColor : 'dark-grey'"
                  :size="20"
                />
              </button>
            </slot>
            <MyMapLayerAdditionnalMenu
              v-model:main-layer="mainLayer"
              :loaded-layer-type="loadedLayerType"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <label class="MyMapLayerPicker__checkbox">
          <input
            ref="checkboxRef"
            type="checkbox"
            v-model="mainLayer.isShown"
            class="MyMapLayerPicker__checkboxInput"
          />
          <div class="MyMapLayerPicker__descCtn">
            <span
              :title="mainLayer.name.length > 15 ? mainLayer.name : undefined"
              v-if="mainLayer.name.length > 15"
            >
              {{ reduceText(mainLayer.name, mainLayer.name ? 15 : 20) }}
            </span>
            <span v-else>{{ mainLayer.name }}</span>
          </div>
        </label>
        <div class="MyMapLayerPicker__actions">
          <MyMapLayerAdditionnalMenu
            v-model:main-layer="mainLayer"
            :loaded-layer-type="loadedLayerType"
            :activator-always-main-blue="primaryParentBorder"
          />
        </div>
      </template>
    </div>
    <div class="MyMapLayerPicker__listBlock" :is-shown="isExpanded" v-if="subLayers">
      <div class="MyMapLayerPicker__listBlockWrapper">
        <label v-for="(subLayer, key) in subLayers" :key="key" class="MyMapLayerPicker__checkbox">
          <input
            type="checkbox"
            v-model="subLayer.isShown"
            @change="changeSubLayer"
            class="MyMapLayerPicker__checkboxInput"
          />
          <div class="MyMapLayerPicker__iconCtn">
            <img
              loading="lazy"
              :src="subLayer.icon"
              :alt="subLayer.name"
              v-if="loadedLayerType === LayerType.ATLAS_LAYER && subLayer.icon"
            />
            <span
              v-if="subLayer.name.length > 25"
              :title="subLayer.name"
              class="text-capitalize"
              :class="{
                'ml-1': loadedLayerType === LayerType.ATLAS_LAYER && subLayer.icon,
                MyMapLayerPicker__highlightedText: isNameInSearchText(subLayer.name),
              }"
            >
              {{ reduceText(subLayer.name, 25) }}
            </span>
            <span
              v-else
              class="text-capitalize"
              :class="{
                'ml-1': loadedLayerType === LayerType.ATLAS_LAYER && subLayer.icon,
                MyMapLayerPicker__highlightedText: isNameInSearchText(subLayer.name),
              }"
            >
              {{ subLayer.name }}
            </span>
          </div>
        </label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { LayerType } from '@/models/enums/geo/LayerType'
import type { Layer } from '@/models/interfaces/map/Layer'
import type { TypeOuvrageMarkerConfig } from '@/models/interfaces/map/MarkerConfig'
import { debounce, reduceText } from '@/services/utils/UtilsService'
import { isMarkerConfigEmpty } from '@/services/map/TypeOuvrageMarkerService'
import { useMyMapStore } from '@/stores/myMapStore'
import { computed, ref, watch, type ModelRef, nextTick } from 'vue'
import Icon from '@/components/ui/Icon.vue'
import LayerStyleSwatch from '@/views/map/components/LayerStyleSwatch.vue'
import MyMapLayerAdditionnalMenu from './MyMapLayerAdditionnalMenu.vue'

const isExpanded = ref(false)
const isIndicatorStyle = computed(() => props.loadedLayerType === LayerType.OSM_LAYER)
const categoryIconColor = computed(() => props.categoryColor ?? 'main-blue')

function toggleOsmLayer() {
  if (!mainLayer.value) return
  mainLayer.value = { ...mainLayer.value, isShown: !mainLayer.value.isShown }
  emits('update')
}
const mainLayer: ModelRef<Layer | undefined> = defineModel('mainLayer')
const subLayers: ModelRef<Layer[] | undefined> = defineModel('subLayers')
const hasMarkerConfig = computed(
  () => !isMarkerConfigEmpty(mainLayer.value?.style?.markerConfig as TypeOuvrageMarkerConfig | null | undefined),
)
const myMapStore = useMyMapStore()
const emits = defineEmits(['update'])
const checkboxRef = ref<HTMLInputElement | null>(null)

const props = withDefaults(
  defineProps<{
    loadedLayerType?: LayerType
    categoryColor?: string
    /** Platform accordion main layer row: MyMapDomaine-like card (neutral border, h6 label). */
    primaryParentBorder?: boolean
    /** Shown in italics under the layer name (OSM indicator rows only). */
    dataSourceLabel?: string
    /** Shown as a pill badge under the layer name (OSM indicator rows only). Takes priority over dataSourceLabel. */
    countLabel?: number | string
  }>(),
  {
    loadedLayerType: LayerType.APP_LAYER,
    categoryColor: undefined,
    primaryParentBorder: false,
    dataSourceLabel: undefined,
    countLabel: undefined,
  },
)

const disabledLayers = computed(() => {
  return subLayers.value?.filter((subLayer: Layer) => subLayer.isShown === false) ?? []
})

const isIndeterminate = computed(() => {
  if (subLayers.value == undefined) return false
  return disabledLayers.value.length !== 0 && disabledLayers.value.length !== subLayers.value.length
})

// Update indeterminate state on checkbox
watch(
  [isIndeterminate, () => mainLayer.value],
  async () => {
    await nextTick()
    const checkbox = checkboxRef.value
    if (checkbox) {
      checkbox.indeterminate = isIndeterminate.value
    }
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => mainLayer.value?.isShown,
  (newValue) => {
    editAllSubLayers(newValue)
  },
)

watch(
  () => mainLayer.value?.opacity,
  (newValue) => {
    if (!mainLayer.value || newValue === undefined) return
    // Pour les couches OSM, l'opacité est gérée par le composant parent via l'événement @update
    // car il faut construire un objet OsmLayer avec geometryType, tableName, etc.
    if (props.loadedLayerType === LayerType.OSM_LAYER) {
      // Émettre un événement pour que le parent gère l'opacité
      emits('update')
      return
    }
    changeLayerOpacity(mainLayer.value, newValue)
  },
)

watch(
  () => myMapStore.atlasSearchText,
  (newValue) => {
    if (newValue) {
      if (subLayers.value && props.loadedLayerType === LayerType.ATLAS_LAYER) {
        if (myMapStore.atlasSearchText.length >= 3) {
          if (subLayers.value.some((subLayer) => isNameInSearchText(subLayer.name))) {
            isExpanded.value = true
          } else {
            isExpanded.value = false
          }
        } else {
          isExpanded.value = false
        }
      }
    } else {
      if (subLayers.value && props.loadedLayerType === LayerType.ATLAS_LAYER) {
        isExpanded.value = false
      }
    }
  },
)

const changeLayerOpacity = debounce(async (layer: Layer, opacityPercentage: number) => {
  if (mainLayer.value) {
    const opacity = opacityPercentage / 100
    const paintProperty =
      props.loadedLayerType === LayerType.APP_LAYER ? 'icon-opacity' : 'raster-opacity'
    myMapStore.myMap?.setPaintProperty(layer.id.toString(), paintProperty, opacity)
  }
}, 100)

const changeSubLayer = () => {
  if (mainLayer.value) {
    if (disabledLayers.value.length === 0) {
      mainLayer.value.isShown = true
    } else if (disabledLayers.value.length === subLayers.value?.length) {
      mainLayer.value.isShown = false
    }
  }
  emits('update')
}

const editAllSubLayers = (show = true) => {
  if (subLayers.value != null && !myMapStore.deserializedMapState) {
    const layers = subLayers.value
    layers.forEach((subLayer, key) => {
      if (layers[key]) {
        layers[key].isShown = show
      }
    })
  }
  emits('update')
}

function isNameInSearchText(name: string): boolean {
  if (!myMapStore.atlasSearchText) return false
  return name.toLowerCase().includes(myMapStore.atlasSearchText.toLowerCase())
}
</script>

<style lang="scss">
.MyMapLayerPicker {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;

  .MyMapLayerPicker__parentBlock {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.25rem 0.5rem;
    border: solid 1px rgb(var(--v-theme-dark-grey));
    border-radius: var(--dim-radius);

    /* Main layer row inside platform accordions: same card treatment as MyMapDomaine. */
    &--platformInnerRow {
      padding: 0.75rem 1rem;
      min-height: var(--panel-row-min-height);
      box-sizing: border-box;
      background: rgb(var(--v-theme-surface));
      border: 1px solid var(--neutral-border);

      .MyMapLayerPicker__descCtn {
        margin: 0 0.25rem;
        flex: 1;
        min-width: 0;

        span:not(.MyMapLayerPicker__labelFull) {
          font-size: var(--font-size-h6);
          font-weight: 600;
        }
      }
    }

    .MyMapLayerPicker__descCtn {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      gap: 0.5rem;
      margin: 0 0.25rem;
      flex: 1 0 auto;
      span:not(.MyMapLayerPicker__labelFull) {
        color: rgb(var(--v-theme-main-blue));
        font-weight: bold;
        font-size: var(--font-size-sm);
      }
    }
    .MyMapLayerPicker__actions {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;

      .MyMapLayerPicker__additionnalMenu {
        margin-right: -0.375rem;
      }
    }
  }

  &--indicatorStyle {
    min-width: 0;

    .MyMapLayerPicker__parentBlock {
      border: none;
      border-radius: 0;
      cursor: pointer;
    }

    .MyMapIndicateur__content {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex: 1;
      min-width: 0;
      cursor: pointer;
    }

    .MyMapIndicateur__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 1.75rem;
      height: 1.75rem;
    }

    .MyMapLayerPicker__checkbox.MyMapIndicateur__text {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-flow: column nowrap;
      align-items: flex-start;
      text-align: left;
    }

    .MyMapIndicateur__titre {
      min-width: 0;
      text-align: left;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.125rem;
    }

    .MyMapIndicateur__titreText {
      word-break: break-word;
      text-align: left;
    }

    .MyMapLayerPicker__dataSource {
      font-size: 0.6875rem;
      font-style: italic;
      font-weight: 400;
      line-height: 1.2;
      color: rgba(var(--v-theme-on-surface), 0.62);
    }

    .MyMapLayerPicker__countBadge {
      font-size: 0.7rem;
      font-weight: 700;
      color: rgba(var(--v-theme-main-blue), 0.75);
      background: rgba(var(--v-theme-main-blue), 0.1);
      border-radius: 999px;
      padding: 0.08rem 0.45rem;
      min-width: 1.3rem;
      text-align: center;
      width: fit-content;
    }

    .MyMapIndicateur__actionsColumn {
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      justify-content: center;
      gap: 0.15rem;
      flex-shrink: 0;
    }

    .MyMapIndicateur__toggleButton {
      flex-shrink: 0;
    }
  }

  .MyMapLayerPicker__checkboxInput--hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  .MyMapLayerPicker__iconCtn {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    img {
      width: 1rem;
    }
  }

  .MyMapLayerPicker__listBlock {
    display: grid;
    grid-template-rows: 0fr;
    transition: all 0.15s ease-in;
    border-radius: var(--dim-radius);
    border: solid 1px transparent;
    max-height: 11.5rem;

    &[is-shown='true'] {
      grid-template-rows: 1fr;
      border: solid 1px rgb(var(--v-theme-main-grey));
      .MyMapLayerPicker__listBlockWrapper {
        padding: 0.5rem 0.75rem;
      }
    }

    .MyMapLayerPicker__listBlockWrapper {
      padding: 0 0.75rem;
      display: flex;
      border-color: transparent;
      flex-flow: column nowrap;
      transition: all 0.15s ease-in;
      gap: 0.25rem;
      overflow-y: auto;
      label {
        font-size: var(--font-size-sm);
        opacity: 1;
      }
    }
  }

  .MyMapLayerPicker__checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: var(--font-size-sm);
  }

  .MyMapLayerPicker__checkboxInput {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: rgb(var(--v-theme-main-blue));
  }

  .MyMapLayerPicker__expandBtn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;
  }

  .MyMapLayerPicker__expandBtn:hover {
    background-color: var(--color-main-hover);
  }

  .MyMapLayerPicker__highlightedText {
    color: rgb(var(--v-theme-main-blue));
    font-weight: bold;
    font-size: 1.1rem;
    text-decoration: underline;
  }
}
</style>
